import { useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation, useTranslate, useDataProvider } from "@refinedev/core";
import { aiWizardVoorstel, useAchtergrondTaak, VoorstelFout } from "./achtergrondAi";
import {
  DateField,
  ActionBar,
  Button,
  CheckboxField,
  Field,
  FormCard,
  SectionHeader,
  SelectField,
  StatusBadge,
  TextareaField,
} from "@fawkes/ui";

import type { SettingsContext } from "./tiles/types";
import "./WizardBuilderSettings.css";

// jve-wizard (Δ2 Phase 2): the React port of the Jmix `wizard-builder` (settings "Registraties &
// Wizards" tile / WizardBeheerView). Drives GET /api/wizard-definitions (+ /{id}) and POST
// /api/wizard-definitions/action — the same coarse "load payload + applyAction(JSON)" contract the Jmix
// postMessage bridge used, so the bridge is swapped for fetch. Covers the definition list, the step→field
// tree editor (add/edit/delete + up/down reorder), the tile config, and the publish/version lifecycle.
// Deferred (api returns 400): the AI structure generator. Drag-and-drop reorder is a follow-up — up/down
// move buttons use the api's step.move/field.move here.

interface Optie {
  id: string;
  label: string;
}
interface Veld {
  id: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  required: boolean;
  helpText: string | null;
  sortOrder: number;
  /** Paren, of kale teksten uit oudere lijsten; zie {@link keuzeTekst}. */
  options: Array<{ label: string; value: string } | string>;
  scale?: boolean | null;
  target?: string | null;
}
interface Stap {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  velden: Veld[];
}
interface StatusRuleEntry {
  kleur: string;
  label: string;
}
interface StatusRule {
  fieldKey?: string;
  map?: Record<string, StatusRuleEntry>;
  default?: StatusRuleEntry;
}
interface Tegel {
  id: string;
  title: string | null;
  icon: string | null;
  visibleFieldKeys: string[];
  statusRule?: StatusRule | null;
  active: boolean;
}
// Nazorg rule shapes (nazorg-authoring epic): the rode-vlag-regel and score-mapping collections the api
// detail payload carries for a nazorg-vragenlijst (nazorgType = "nazorg"). Vocabulary ids come from the
// payload's opties (server-side labels) — never invented client-side, they are baked into the issuance
// snapshot the public portal evaluates.
interface VlagRegel {
  id: string;
  fieldKey: string;
  operator: string;
  waarde: string | null;
  kleur: string;
  sortOrder?: number | null;
}
interface ScoreMapping {
  id: string;
  fieldKey: string;
  dimensie: string;
  weging: number;
}
interface Definitie {
  id: string;
  name: string;
  targetType: string;
  category: string | null;
  species: string | null;
  description: string | null;
  status: string;
  active: boolean;
  publishedVersionNumber?: number | null;
  stappen: Stap[];
  tegel: Tegel | null;
  // Nazorg config (all null/empty for a registration wizard).
  nazorgType?: string | null;
  doelgroep?: string | null;
  contactmoment?: string | null;
  kanaal?: string | null;
  autoVersturen?: boolean | null;
  herinnerNaDagen?: number | null;
  sluitNaDagen?: number | null;
  vlagRegels?: VlagRegel[];
  scoreMapping?: ScoreMapping[];
  /** Gevuld: de wizard is een verzorgingslogboek en schrijft in deze logboekcategorie. */
  logboekCategorie?: string | null;
}
interface Opties {
  targetTypes: Optie[];
  fieldTypes: Optie[];
  diersoorten: Optie[];
  contactmomenten?: Optie[];
  dimensies?: Optie[];
  vlagKleuren?: Optie[];
  vlagOperatoren?: Optie[];
}
interface DefinitieRow {
  id: string;
  name: string;
  targetType: string;
  status: string;
  active: boolean;
  publishedVersionNumber?: number | null;
  stapAantal: number;
  nazorgType?: string | null;
  contactmomentLabel?: string | null;
}
interface ListResp {
  definities: DefinitieRow[];
  opties: Opties;
}
interface DetailResp {
  definitie: Definitie;
  opties: Opties;
}

// Tones mirror the Jmix STATUS_KLASSE (CONCEPT = grijs, GEPUBLICEERD = groen, GEARCHIVEERD = rood).
const STATUS_TONE: Record<string, "success" | "neutral" | "danger"> = {
  GEPUBLICEERD: "success",
  CONCEPT: "neutral",
  GEARCHIVEERD: "danger",
};
const STATUS_LABEL: Record<string, string> = {
  GEPUBLICEERD: "Gepubliceerd",
  CONCEPT: "Concept",
  GEARCHIVEERD: "Gearchiveerd",
};

// Per-field-type glyph, mirroring the Jmix wizard-builder VELDTYPES palette icons.
const FIELD_TYPE_ICON: Record<string, string> = {
  TEXT: "📝",
  TEXTAREA: "📄",
  NUMBER: "🔢",
  BOOLEAN: "✅",
  DATE: "📅",
  SELECT: "🔘",
  MULTISELECT: "☑️",
};

// The palette "Bouwstenen", a faithful port of the Jmix wizard-builder VELDTYPES. Each block is both
// draggable onto a step (native HTML5 DnD) and click-to-add to the active step.
const VELDTYPES: Array<{ id: string; icon: string; naam: string; hint: string }> = [
  { id: "TEXT", icon: "📝", naam: "Tekst", hint: "kort antwoord" },
  { id: "TEXTAREA", icon: "📄", naam: "Lang tekstveld", hint: "meerdere regels" },
  { id: "NUMBER", icon: "🔢", naam: "Getal", hint: "bijv. gewicht" },
  { id: "BOOLEAN", icon: "✅", naam: "Ja / nee", hint: "aanvinken" },
  { id: "DATE", icon: "📅", naam: "Datum", hint: "kalender" },
  { id: "SELECT", icon: "🔘", naam: "Keuze", hint: "kies er één" },
  { id: "MULTISELECT", icon: "☑️", naam: "Meerkeuze", hint: "kies meerdere" },
];
function veldtype(id: string) {
  return VELDTYPES.find((v) => v.id === id) ?? VELDTYPES[0];
}

// Dier-gegevens a field can write back to on completion (Jmix wizard-builder DOELVELDEN).
const DOELVELDEN: Array<{ value: string; label: string }> = [
  { value: "", label: "Niet koppelen (alleen in de registratie)" },
  { value: "chipnummer", label: "Dier · Chipnummer" },
  { value: "naam", label: "Dier · Naam" },
  { value: "geslacht", label: "Dier · Geslacht" },
  { value: "geboortedatum", label: "Dier · Geboortedatum" },
  { value: "gewicht", label: "Dier · Gewicht (nieuwe meting)" },
];

// DnD MIME-ish keys, mirroring the Jmix builder so the interaction reads identically.
const DT_NEW = "wizard/newfield";
const DT_MOVE = "wizard/movefield";

// Tile status-indicator colours (Jmix wizard-builder STATUS_KLEUREN). The key is the engine value
// stored in statusRule; the dot colour is token-driven via the `settings-wizard__status-dot--<kleur>`.
const STATUS_KLEUREN: Array<{ value: string; label: string }> = [
  { value: "groen", label: "Groen" },
  { value: "oranje", label: "Oranje" },
  { value: "rood", label: "Rood" },
  { value: "grijs", label: "Grijs" },
];

type ApplyFn = (values: Record<string, unknown>, after?: (id: string | null) => void) => void;

function opts(list: Optie[]) {
  return list.map((o) => ({ value: o.id, label: o.label }));
}
function labelOf(list: Optie[], id: string | null | undefined) {
  if (!id) return "—";
  return list.find((o) => o.id === id)?.label ?? id;
}

/**
 * Deep-open request for the builder (nazorg-authoring epic): open one definition directly in the
 * editor, or create a fresh nazorg-vragenlijst and open it. Used by the NazorgBeheer "Vragenlijsten"
 * tab, which navigates here through App → SettingsDashboard (state nav, consumed once).
 */
export interface WizardBuilderInit {
  openDefinitionId?: string;
  createNazorg?: boolean;
  /**
   * Waar je vandaan kwam toen je deze vragenlijst opende.
   *
   * <p>De bouwer woont in Instellingen, maar je komt er zelden vandaan: je zit in de wachtlijst of in
   * nazorg, klikt op een vragenlijst, en belandt hier. Een terugknop die dan naar de wizardlijst gaat
   * zet je ergens neer waar je niet was, en je bent je plek kwijt (Maarten, 26-10-2026).
   *
   * <p>Geldt alleen zolang je op de vragenlijst staat die je opende. Blader je zelf door naar de
   * lijst, dan is die herkomst niet meer waar en wijst de knop weer gewoon naar Wizards.
   */
  terug?: { label: string; naar: () => void };
}

export function WizardBuilderSettings({
  context,
  onBack,
  init,
  alleenKennismaking = false,
}: {
  context?: SettingsContext;
  onBack: () => void;
  init?: WizardBuilderInit;
  /**
   * Alleen de kennismakingsvragen tonen, zonder de registratiewizards eromheen.
   *
   * <p>Zo staat dit scherm op twee plekken met dezelfde editor: hier onder Instellingen voor wie
   * wizards bouwt, en als Portaalinstellingen bij Advertenties voor wie DierMatch beheert. Dat is
   * bewust geen tweede editor -- vragen maak je op een plek, anders lopen ze uit elkaar.
   */
  alleenKennismaking?: boolean;
}) {
  const t = useTranslate();
  const list = useCustom<ListResp>({ url: "wizard-definitions", method: "get" });
  const availability = useCustom<{ available: boolean }>({
    url: "wizard-definitions/ai-availability",
    method: "get",
  });
  const [selectedId, setSelectedId] = useState<string | null>(init?.openDefinitionId ?? null);
  // De vragenlijst waarmee je binnenkwam -- de enige waarvoor de herkomst van `init.terug` klopt.
  // Een met `createNazorg` net gemaakte lijst telt ook mee; die wordt hieronder ingevuld.
  const binnengekomenOp = useRef<string | null>(init?.openDefinitionId ?? null);
  // Een AI-wizard die op de achtergrond klaar is en waarvoor iemand "Bekijken" koos: openen in de editor.
  const aiWizard = useAchtergrondTaak(aiWizardVoorstel);
  useEffect(() => {
    if (aiWizard.status === "klaar" && aiWizard.bekijken) {
      void list.refetch();
      setSelectedId(aiWizard.resultaat);
      aiWizardVoorstel.overgenomen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen reageren op het voorstel zelf
  }, [aiWizard]);
  const detail = useCustom<DetailResp>({
    url: `wizard-definitions/${selectedId}`,
    method: "get",
    queryOptions: { enabled: Boolean(selectedId) },
  });
  const { mutate } = useCustomMutation();
  const aiBeschikbaar = Boolean(availability.data?.data.available);

  function apply(values: Record<string, unknown>, after?: (resultId: string | null) => void) {
    mutate(
      { url: "wizard-definitions/action", method: "post", values },
      {
        onSuccess: (resp) => {
          const resultId = (resp?.data as { resultId?: string } | undefined)?.resultId ?? null;
          Promise.all([
            list.refetch(),
            selectedId ? detail.refetch() : Promise.resolve(undefined),
          ]).then(() => after?.(resultId));
        },
      },
    );
  }

  // nazorg-authoring: the NazorgBeheer "Nieuwe nazorg-vragenlijst" hand-off — create the concept
  // questionnaire (Jmix createNazorgDefinition defaults) once on mount and open it in the editor.
  const nazorgCreated = useRef(false);
  useEffect(() => {
    if (init?.createNazorg && !nazorgCreated.current) {
      nazorgCreated.current = true;
      apply({ actie: "definition.createNazorg" }, (id) => {
        if (id) {
          binnengekomenOp.current = id;
          setSelectedId(id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume the init exactly once, on mount
  }, []);

  if (list.isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (list.isError) return <p className="fawkes-error">Kon de wizards niet laden.</p>;

  const opties = (detail.data?.data.opties ?? list.data?.data.opties) ?? {
    targetTypes: [],
    fieldTypes: [],
    diersoorten: [],
  };

  // ── Editor (a definition is selected) ──────────────────────────────────
  if (selectedId && detail.data?.data) {
    return (
      <WizardEditor
        def={detail.data.data.definitie}
        opties={opties}
        onApply={apply}
        // Terug naar waar je vandaan kwam, maar alleen voor de vragenlijst waarmee je binnenkwam.
        // Koos je daarna zelf een andere uit de lijst, dan is "terug" die lijst en niet je oude plek.
        terug={init?.terug && selectedId === binnengekomenOp.current ? init.terug : undefined}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  // ── List ───────────────────────────────────────────────────────────────
  const rows = list.data?.data.definities ?? [];
  // Kennismakingsvragenlijsten horen niet tussen de registratiewizards: ze gaan niet over een dier
  // dat je inboekt maar over iemand die op DierMatch op "Ik wil ... leren kennen" klikt. Ze staan
  // hieronder in hun eigen blok, met dezelfde editor -- zo is er maar een plek waar je vragen maakt.
  const registratieRows = rows.filter((row) => !row.nazorgType && row.targetType !== "KENNISMAKING");
  const kennismakingRows = rows.filter((row) => row.targetType === "KENNISMAKING");

  /* De vragen achter "Ik wil ... leren kennen" op DierMatch. Een lijst per diersoort, en bewust
     niet per ras of per dier: formulieren die niemand meer onderhoudt helpen niemand. Wat je hier
     wijzigt staat binnen een paar minuten op de site.

     Dit blok verbergt zich niet als er nog niets staat. Dat deed het wel, en juist op een verse
     omgeving -- waar nog geen lijst bestaat -- was er dan niets te zien en niets te klikken, ook
     niet voor wie wist dat de functie er was. Leeg is hier een toestand met een uitweg, geen reden
     om weg te blijven. */
  const kennismakingBlok = (
    <div className="settings-wizard__list">
      <SectionHeader>Kennismakingsvragen (DierMatch)</SectionHeader>
      <p className="fawkes-muted settings-wizard__lead">
        Wat een belangstellende invult nadat hij op &ldquo;Ik wil &hellip; leren kennen&rdquo;
        klikt. Een lijst per diersoort. De antwoorden komen binnen bij de advertentie van het
        dier, onder Reacties &mdash; er wordt geen relatie van aangemaakt.
      </p>
      <KennismakingBeheer
        aantalLijsten={kennismakingRows.length}
        onVeranderd={() => void list.refetch()}
      />
      {kennismakingRows.map((row) => (
        <div key={row.id} className="settings-wizard__row">
          <div className="settings-wizard__row-main">
            <span className="settings-wizard__row-title">{row.name}</span>
            <StatusBadge tone={STATUS_TONE[row.status] ?? "neutral"}>
              {STATUS_LABEL[row.status] ?? row.status}
            </StatusBadge>
            <span className="fawkes-muted">{row.stapAantal} stap(pen)</span>
          </div>
          <div className="settings-wizard__row-actions">
            {/* Geen verwijderknop: er is er precies een per diersoort, en wie hem weghaalt zet
                de knop op DierMatch stilletjes terug naar alleen het contactblok. Leeghalen kan
                in de editor, zichtbaar en met een versie erbij. */}
            <Button variant="secondary" onClick={() => setSelectedId(row.id)}>
              {t("common.edit")}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  // ── Portaalinstellingen (vanaf het advertentieoverzicht) ───────────────
  // Hetzelfde blok, maar dan als eigen scherm naast de advertenties in plaats van weggestopt
  // tussen de registratiewizards. Wie advertenties beheert zoekt de vragen van DierMatch daar, en
  // niet onder Instellingen > Wizards (Maarten, 2026-10-29).
  if (alleenKennismaking) {
    return (
      <FormCard>
        {/* De terugknop hoort bovenaan en niet alleen onder de lijsten: wie zes vragenlijsten ver
            heeft gescrold wil terug zonder eerst de hele pagina door. Dezelfde plek als op het
            advertentiescherm (Maarten, 2026-10-29). */}
        <div className="settings-wizard__terug">
          <Button variant="secondary" onClick={onBack}>
            ← {t("advertentieListView.title")}
          </Button>
        </div>
        <div className="settings-wizard__header">
          <span className="settings-wizard__header-icon" aria-hidden="true">
            🌍
          </span>
          <div className="settings-wizard__header-text">
            <SectionHeader>Portaalinstellingen</SectionHeader>
            <p className="fawkes-muted settings-wizard__breadcrumb">
              Advertenties › Portaalinstellingen
            </p>
            <p className="fawkes-muted settings-wizard__lead">
              Wat er op DierMatch en ikzoekbaas.nl gebeurt met de dieren die je publiceert.
              {/* Het overzicht hiervoor filtert op vestiging, dit scherm niet. Zonder deze zin
                  verwacht je dat je de lijsten van één opvang aanpast -- en pas je ze voor
                  allemaal aan (Maarten, 2026-10-29). */}{" "}
              <strong>Voor de hele organisatie</strong>, niet per vestiging: alle opvangen werken
              met dezelfde lijsten.
            </p>
          </div>
        </div>

        {kennismakingBlok}

        <ActionBar>
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
        </ActionBar>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <div className="settings-wizard__header">
        <span className="settings-wizard__header-icon" aria-hidden="true">
          🧩
        </span>
        <div className="settings-wizard__header-text">
          <SectionHeader>Registraties &amp; Wizards</SectionHeader>
          <p className="fawkes-muted settings-wizard__breadcrumb">
            {`${t("instellingen.title")}${context ? ` › ${context.label}` : ""} › Wizards`}
          </p>
          <p className="fawkes-muted settings-wizard__lead">
            Maak en publiceer configureerbare registratiewizards (stappen en velden) met een dynamische
            tegel op het dier.
          </p>
        </div>
      </div>

      {aiBeschikbaar && <AiStructureBlock />}

      <div className="settings-wizard__list">
        {/* Nazorg-vragenlijsten beheer je onder Nazorg › Vragenlijsten; hier staan alleen de registratiewizards. */}
        {registratieRows.map((row) => (
          <div key={row.id} className="settings-wizard__row">
            <div className="settings-wizard__row-main">
              <span className="settings-wizard__row-title">{row.name}</span>
              {row.nazorgType ? (
                // A nazorg-vragenlijst: show the aftercare contact moment instead of the target type.
                <span className="fawkes-muted">
                  {t("wizards.nazorg.rowBadge")}
                  {row.contactmomentLabel ? ` · ${row.contactmomentLabel}` : ""}
                </span>
              ) : (
                <span className="fawkes-muted">{labelOf(opties.targetTypes, row.targetType)}</span>
              )}
              <StatusBadge tone={STATUS_TONE[row.status] ?? "neutral"}>
                {STATUS_LABEL[row.status] ?? row.status}
              </StatusBadge>
              <span className="fawkes-muted">{row.stapAantal} stap(pen)</span>
            </div>
            <div className="settings-wizard__row-actions">
              <Button variant="secondary" onClick={() => setSelectedId(row.id)}>
                {t("common.edit")}
              </Button>
              <Button variant="secondary" onClick={() => apply({ actie: "definition.delete", id: row.id })}>
                {t("common.delete")}
              </Button>
            </div>
          </div>
        ))}
        {registratieRows.length === 0 && <p className="fawkes-muted">Nog geen wizards.</p>}
      </div>

      {/* De kennismakingsvragen staan sinds 2026-10-29 bij de advertenties, want daar beheer je
          wat er op DierMatch staat. Deze regel blijft staan voor wie ze hier zocht; hij verdwijnt
          zodra er geen kennismakingslijst meer bestaat. */}
      {kennismakingRows.length > 0 && (
        <p className="fawkes-muted settings-wizard__lead">
          De kennismakingsvragen van DierMatch ({kennismakingRows.length} lijsten) staan bij
          Advertenties › Portaalinstellingen.
        </p>
      )}

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Button
          onClick={() =>
            apply({ actie: "definition.create", name: "Nieuwe wizard" }, (id) => id && setSelectedId(id))
          }
        >
          Nieuwe wizard
        </Button>
      </ActionBar>
    </FormCard>
  );
}

interface KennismakingStatus {
  lijsten: number;
  gepubliceerd: number;
  automatisch: boolean;
}

interface KennismakingUitslag {
  klaargezet: number;
  verstuurd: number;
  opgehaald: number;
  /** Waarom het contact met DierMatch misging; null als het goed ging. */
  fout?: string | null;
}

/**
 * Het kennismaken aan de praat krijgen en houden, vanuit het scherm.
 *
 * <p>Hiervoor zat alles achter twee serverinstellingen: zonder die vlaggen bestonden er geen
 * startlijsten en ging er niets naar DierMatch. Een asiel kan daar niet bij, en wie het wel kan
 * moet eerst een wrapper-configuratie bewerken en een service herstarten om te zien of zijn werk
 * aankomt. De vlaggen gaan over het achtergrondproces; of de functie te gebruiken is, hoort er niet
 * van af te hangen.
 *
 * <p>De knoppen bewaren niets en zetten niets aan. Ze doen eenmalig wat de scheduler anders zelf
 * zou doen, en vertellen daarna wat er gebeurd is -- "6 klaargezet, 6 verstuurd" is een antwoord,
 * een stil scherm niet.
 */
function KennismakingBeheer({
  aantalLijsten,
  onVeranderd,
}: {
  aantalLijsten: number;
  onVeranderd: () => void;
}) {
  const status = useCustom<KennismakingStatus>({ url: "kennismaking/beheer/status", method: "get" });
  const { mutate } = useCustomMutation();
  const [bezig, setBezig] = useState<"klaarzetten" | "versturen" | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [misgegaan, setMisgegaan] = useState(false);
  const automatisch = status.data?.data.automatisch ?? true;

  function doe(wat: "klaarzetten" | "versturen") {
    setBezig(wat);
    setMelding(null);
    setMisgegaan(false);
    mutate(
      {
        url: `kennismaking/beheer/${wat === "klaarzetten" ? "standaardlijsten" : "synchroniseer"}`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (resp) => {
          const u = resp?.data as KennismakingUitslag | undefined;
          setBezig(null);
          setMisgegaan(Boolean(u?.fout));
          setMelding(zinVan(wat, u));
          void status.refetch();
          onVeranderd();
        },
        onError: () => {
          setBezig(null);
          setMisgegaan(true);
          setMelding(
            "Dat lukte niet. Staat de koppeling met DierMatch goed ingesteld? De serverlog vertelt waarom.",
          );
        },
      },
    );
  }

  return (
    <div className="settings-wizard__kennis">
      {aantalLijsten === 0 ? (
        <>
          <p>
            Er staan nog geen vragenlijsten klaar. Zet de standaardlijsten neer en je hebt er een
            per diersoort &mdash; hond, kat, konijn, knaagdier, vogel en overig &mdash; van zo&apos;n
            twintig vragen, verdeeld over vier stappen.
          </p>
          <p className="fawkes-muted">
            Het zijn startlijsten: je past ze hierna aan zoals je wilt, en wat jij wijzigt wordt
            nooit meer overschreven.
          </p>
        </>
      ) : (
        <p>
          {aantalLijsten} vragenlijst{aantalLijsten === 1 ? "" : "en"}, waarvan{" "}
          {status.data?.data.gepubliceerd ?? 0} gepubliceerd. Alleen een gepubliceerde lijst staat op
          DierMatch achter de knop &ldquo;Ik wil &hellip; leren kennen&rdquo;.
        </p>
      )}

      {/* Op een site staan de dieren van meer dan een organisatie. Zonder deze regel lijkt het
          alsof de knop stuk is als je op een dier van een ander asiel klikt en daar geen formulier
          komt -- dat overkwam ons op acc, waar 125 dieren van een andere backoffice komen. */}
      <p className="fawkes-muted">
        De lijsten gelden voor de dieren die vanuit deze backoffice op de site staan. Dieren van een
        andere organisatie op dezelfde site houden hun eigen lijst, of geen.
      </p>

      {automatisch ? (
        <p className="fawkes-muted">
          Wat je hier wijzigt gaat vanzelf naar DierMatch, en ingevulde formulieren komen vanzelf
          binnen &mdash; meestal binnen vijf minuten. Je hoeft nergens op te drukken.
        </p>
      ) : (
        <p className="settings-wizard__kennis-let-op">
          Het automatisch bijwerken staat uit op deze server. Je wijzigingen gaan pas naar DierMatch
          als je hieronder op de knop drukt, en ingevulde formulieren komen pas dán binnen &mdash;
          ook een reactie die er al een tijdje ligt.
        </p>
      )}

      {melding && (
        <p
          className={
            misgegaan ? "settings-wizard__kennis-let-op" : "settings-wizard__kennis-melding"
          }
        >
          {melding}
        </p>
      )}

      {/* Helemaal weg als er niets te drukken valt: een lege knoppenbalk laat een gat achter. */}
      {(aantalLijsten === 0 || !automatisch) && (
      <div className="settings-wizard__kennis-acties">
        {aantalLijsten === 0 && (
          <Button onClick={() => doe("klaarzetten")} disabled={bezig !== null}>
            {bezig === "klaarzetten" ? "Bezig…" : "Standaardlijsten klaarzetten"}
          </Button>
        )}
        {/* Alleen als het níét vanzelf gaat. Een knop die doet wat er elke vijf minuten toch al
            gebeurt, nodigt uit om hem te blijven indrukken en wekt de indruk dat het zonder niet
            werkt. Staat de scheduler uit op deze server, dan is hij wél de enige weg. */}
        {!automatisch && (
          <Button variant="secondary" onClick={() => doe("versturen")} disabled={bezig !== null}>
            {bezig === "versturen" ? "Bezig…" : "Vragen versturen en reacties ophalen"}
          </Button>
        )}
      </div>
      )}
    </div>
  );
}

/** Wat er gebeurd is, in gewone taal -- inclusief het geval waarin er niets te doen bleek. */
function zinVan(wat: "klaarzetten" | "versturen", u: KennismakingUitslag | undefined) {
  if (!u) {
    return "Gelukt.";
  }
  const delen: string[] = [];
  if (wat === "klaarzetten") {
    delen.push(
      u.klaargezet === 0
        ? "Er waren al lijsten voor elke diersoort"
        : `${u.klaargezet} lijst${u.klaargezet === 1 ? "" : "en"} klaargezet`,
    );
  }
  // Een mislukte verbinding is geen "niets te versturen". Zeggen wat er misging is het halve
  // antwoord; zwijgen laat iemand in de verkeerde hoek zoeken.
  if (u.fout) {
    delen.push(`maar DierMatch antwoordde niet goed (${u.fout})`);
    return `${delen.join(", ")}.`;
  }
  delen.push(
    u.verstuurd === 0
      ? "niets te versturen"
      : `${u.verstuurd} naar DierMatch gestuurd`,
  );
  if (u.opgehaald > 0) {
    delen.push(`${u.opgehaald} nieuwe reactie${u.opgehaald === 1 ? "" : "s"} opgehaald`);
  }
  return `${delen.join(", ")}.`;
}

/**
 * The "Maak een wizard met AI" assistant (port of the Jmix `wizard-builder` aiBlok): a free-text
 * description is sent to POST /api/wizard-definitions/ai-structure (WizardAiService.suggestStructure →
 * createFromAi), which builds a CONCEPT wizard and returns its id; the SPA opens it in the editor for the
 * admin to review and publish. Shown only when an AI provider is configured (ai-availability).
 */
function AiStructureBlock() {
  const dataProvider = useDataProvider();
  const lopend = useAchtergrondTaak(aiWizardVoorstel);
  const [beschrijving, setBeschrijving] = useState(lopend.status === "fout" ? lopend.beschrijving : "");
  const busy = lopend.status === "bezig";

  // Een wizard bouwen duurt al snel een minuut: het loopt op de achtergrond (aiWizardVoorstel). De melding
  // rechtsonder toont de voortgang; "Bekijken" opent de nieuwe conceptwizard hier in de editor.
  function maak() {
    const provider = dataProvider();
    void aiWizardVoorstel.start(beschrijving.trim(), async (b) => {
      const resp = await provider.custom!({
        url: "wizard-definitions/ai-structure",
        method: "post",
        payload: { beschrijving: b },
      });
      const result = resp.data as unknown as { beschikbaar: boolean; resultId: string | null };
      if (!result.resultId) {
        throw new VoorstelFout(
          result.beschikbaar
            ? "De AI kon hier nog geen wizard van maken. Probeer het korter of concreter."
            : "De AI-hulp is nu niet beschikbaar. Maak de wizard handmatig aan.",
        );
      }
      return result.resultId;
    });
    setBeschrijving("");
  }
  const message =
    lopend.status === "bezig"
      ? "De AI bouwt de wizard op de achtergrond. Je kunt verder werken; rechtsonder zie je wanneer hij klaar is."
      : lopend.status === "fout"
        ? (lopend.melding ?? "Het maken van de wizard is mislukt. Probeer het opnieuw.")
        : null;

  return (
    <div className="settings-wizard__ai">
      <SectionHeader>Maak een wizard met AI</SectionHeader>
      <p className="fawkes-muted">Beschrijf in gewone taal welk registratieformulier je wilt maken.</p>
      <TextareaField
        label="Omschrijving"
        value={beschrijving}
        onChange={(e) => setBeschrijving(e.target.value)}
        placeholder="bijv. Een intakecontrole voor katten met een stap Algemeen (chipnummer, gewicht, geslacht) en een stap Gezondheid (gevaccineerd ja/nee, vlooien ja/nee, opmerkingen)."
      />
      <div className="settings-wizard__ai-actions">
        <Button disabled={busy || !beschrijving.trim()} onClick={maak}>
          {busy ? "AI bouwt op de achtergrond…" : "✨ Maak met AI"}
        </Button>
      </div>
      {message && <p className="fawkes-muted">{message}</p>}
    </div>
  );
}

/** Gewone wizard, of een verzorgingslogboek in een van de logboekcategorieën. */
const LOGBOEK_KEUZES = [
  { value: "", label: "Nee, een gewone wizard" },
  { value: "VERZORGING", label: "Ja, als Verzorging" },
  { value: "SCHOONMAAK", label: "Ja, als Schoonmaak" },
  { value: "VOEDING", label: "Ja, als Voeding" },
  { value: "WATER", label: "Ja, als Water" },
  { value: "UITLATEN", label: "Ja, als Uitlaten" },
  { value: "MEDICATIE", label: "Ja, als Medicatie" },
  { value: "OBSERVATIE", label: "Ja, als Observatie" },
];

function DefinitionMeta({
  def,
  opties,
  onApply,
}: {
  def: Definitie;
  opties: Opties;
  onApply: (values: Record<string, unknown>, after?: (id: string | null) => void) => void;
}) {
  const [name, setName] = useState(def.name);
  const [targetType, setTargetType] = useState(def.targetType);
  const [category, setCategory] = useState(def.category ?? "");
  const [species, setSpecies] = useState(def.species ?? "");
  const [description, setDescription] = useState(def.description ?? "");
  const [logboekCategorie, setLogboekCategorie] = useState(def.logboekCategorie ?? "");

  return (
    <div className="fawkes-form-grid">
      <Field label="Naam" value={name} onChange={(e) => setName(e.target.value)} />
      <SelectField
        label="Koppelen aan"
        value={targetType}
        onChange={(e) => setTargetType(e.target.value)}
        options={opts(opties.targetTypes)}
      />
      <Field label="Categorie" value={category} onChange={(e) => setCategory(e.target.value)} />
      <SelectField
        label="Diersoort"
        value={species}
        onChange={(e) => setSpecies(e.target.value)}
        options={opts(opties.diersoorten)}
      />
      <TextareaField
        label="Omschrijving"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      {!def.nazorgType && (
        // Alma, Roze, 2026-10-09: een dagformulier per diersoort ("schoonmaakformulier kat") hoort in het logboek,
        // niet als zoveelste kaart tussen de wizards.
        <SelectField
          label="Verzorgingslogboek"
          value={logboekCategorie}
          onChange={(e) => setLogboekCategorie(e.target.value)}
          options={LOGBOEK_KEUZES}
        />
      )}
      {!def.nazorgType && (
        <p className="settings-wizard__hint">
          Een verzorgingslogboek start je vanuit de Logboek-tegel van het dier, niet tussen de wizards; elke ingevulde
          ronde komt als regel in het logboek.
        </p>
      )}
      <div className="settings-wizard__meta-actions">
        <Button
          onClick={() =>
            onApply({
              actie: "definition.update",
              id: def.id,
              name,
              targetType,
              category,
              species,
              description,
              ...(def.nazorgType ? {} : { logboekCategorie }),
            })
          }
        >
          Gegevens opslaan
        </Button>
      </div>
    </div>
  );
}

// jve-wizard (DnD slice): the editor is a faithful port of the Jmix `wizard-builder` WizardEditor —
// an editor head (back / title+status / archive+publish), the concept/live status banners, and the
// four Jmix tabs. The "Formulier bouwen" tab is the palette + canvas drag-and-drop builder.
function WizardEditor({
  def,
  opties,
  onApply,
  onBack,
  terug,
}: {
  def: Definitie;
  opties: Opties;
  onApply: ApplyFn;
  onBack: () => void;
  /** Waar je vandaan kwam; ontbreekt hij, dan gaat de knop naar de wizardlijst. */
  terug?: { label: string; naar: () => void };
}) {
  const t = useTranslate();
  const [tab, setTab] = useState<"formulier" | "tegel" | "nazorg" | "algemeen" | "voorbeeld">(
    "formulier",
  );
  const gepubliceerd = def.status === "GEPUBLICEERD";
  // Alleen een échte nazorgvragenlijst, niet elke lijst die het nazorgveld gebruikt. De
  // afstandsvragenlijst deelt die kolom (nazorgType "afstand"), en kreeg daardoor een tabblad over
  // "wanneer de vragenlijst na adoptie uitgaat" -- dat slaat bij afstand nergens op
  // (Maarten, 26-10-2026).
  const isNazorg = def.nazorgType === "nazorg";
  const TABS: Array<[typeof tab, string]> = [
    ["formulier", "Formulier bouwen"],
    // Nazorg-vragenlijst (nazorg-authoring epic): send-config + rode-vlag-regels + score-mapping,
    // mirroring the Jmix wizard-builder's nazorg panels. Registration wizards don't get the tab.
    ...(isNazorg ? ([["nazorg", t("wizards.nazorg.tab")]] as Array<[typeof tab, string]>) : []),
    ["tegel", "Tegel & score"],
    ["algemeen", "Naam & soort"],
    ["voorbeeld", "Voorbeeld"],
  ];

  return (
    <FormCard>
      <div className="settings-wizard__editor-head">
        <Button variant="secondary" onClick={terug ? terug.naar : onBack}>
          ‹ {terug ? terug.label : "Wizards"}
        </Button>
        <div className="settings-wizard__editor-title">
          <strong>{def.name}</strong>
          <StatusBadge tone={STATUS_TONE[def.status] ?? "neutral"}>
            {STATUS_LABEL[def.status] ?? def.status}
            {def.publishedVersionNumber ? ` v${def.publishedVersionNumber}` : ""}
          </StatusBadge>
        </div>
        <div className="settings-wizard__editor-actions">
          {def.status !== "GEARCHIVEERD" && (
            <Button variant="secondary" onClick={() => onApply({ actie: "definition.archive", id: def.id })}>
              Archiveren
            </Button>
          )}
          <Button onClick={() => onApply({ actie: "definition.publish", id: def.id })}>
            {gepubliceerd ? "Nieuwe versie publiceren" : "Publiceren"}
          </Button>
        </div>
      </div>

      {def.status === "CONCEPT" && (
        <div className="settings-wizard__status-banner settings-wizard__status-banner--concept">
          📝 Dit is nog een <strong>concept</strong> — medewerkers kunnen hem pas invullen nadat je
          hierboven op <strong>Publiceren</strong> klikt.
        </div>
      )}
      {def.status === "GEPUBLICEERD" && (
        <div className="settings-wizard__status-banner settings-wizard__status-banner--live">
          ✓ Gepubliceerd{def.publishedVersionNumber ? ` (v${def.publishedVersionNumber})` : ""}. Iets
          gewijzigd? Klik daarna op <strong>Nieuwe versie publiceren</strong> om het door te voeren op
          het dier.
        </div>
      )}

      <div className="settings-wizard__tabs" role="tablist">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`settings-wizard__tab ${tab === k ? "settings-wizard__tab--active" : ""}`}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "formulier" && <FormulierTab def={def} onApply={onApply} />}
      {tab === "nazorg" && isNazorg && <NazorgTab def={def} opties={opties} onApply={onApply} />}
      {tab === "tegel" && <TileEditor def={def} onApply={onApply} />}
      {tab === "algemeen" && <DefinitionMeta def={def} opties={opties} onApply={onApply} />}
      {tab === "voorbeeld" && <PreviewTab def={def} />}
    </FormCard>
  );
}

// ── Tab: Nazorg (nazorg-authoring epic) — send-config + rode-vlag-regels + score-mapping ──────────
// Mirrors the Jmix wizard-builder nazorg panels. Doelgroep ("adoptant") and kanaal ("platform") are
// locked server-side, so they render read-only; the vocabularies (contactmomenten, operatoren,
// kleuren, dimensies) come from the payload's opties — the ids are baked into the issuance snapshot.
function NazorgTab({ def, opties, onApply }: { def: Definitie; opties: Opties; onApply: ApplyFn }) {
  const t = useTranslate();
  const allFields = def.stappen.flatMap((s) => s.velden);
  const veldOpties = allFields.map((f) => ({ value: f.fieldKey, label: f.label }));
  return (
    <div className="settings-wizard__nazorg">
      <NazorgConfig def={def} opties={opties} onApply={onApply} />
      <SectionHeader>{t("wizards.nazorg.vlagRegels.title")}</SectionHeader>
      <p className="fawkes-muted">{t("wizards.nazorg.vlagRegels.lead")}</p>
      {allFields.length === 0 ? (
        <p className="fawkes-muted">{t("wizards.nazorg.geenVelden")}</p>
      ) : (
        <VlagRegelsEditor def={def} opties={opties} veldOpties={veldOpties} onApply={onApply} />
      )}
      <SectionHeader>{t("wizards.nazorg.scoreMapping.title")}</SectionHeader>
      <p className="fawkes-muted">{t("wizards.nazorg.scoreMapping.lead")}</p>
      {allFields.length === 0 ? (
        <p className="fawkes-muted">{t("wizards.nazorg.geenVelden")}</p>
      ) : (
        <ScoreMappingEditor def={def} opties={opties} veldOpties={veldOpties} onApply={onApply} />
      )}
    </div>
  );
}

function NazorgConfig({ def, opties, onApply }: { def: Definitie; opties: Opties; onApply: ApplyFn }) {
  const t = useTranslate();
  const [contactmoment, setContactmoment] = useState(def.contactmoment ?? "");
  const [autoVersturen, setAutoVersturen] = useState(Boolean(def.autoVersturen));
  const [herinner, setHerinner] = useState(def.herinnerNaDagen == null ? "" : String(def.herinnerNaDagen));
  const [sluit, setSluit] = useState(def.sluitNaDagen == null ? "" : String(def.sluitNaDagen));

  function dagen(v: string): number | null {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }

  return (
    <>
      <SectionHeader>{t("wizards.nazorg.config.title")}</SectionHeader>
      <p className="fawkes-muted">{t("wizards.nazorg.config.lead")}</p>
      <div className="fawkes-form-grid">
        <SelectField
          label={t("wizards.nazorg.config.contactmoment")}
          value={contactmoment}
          onChange={(e) => setContactmoment(e.target.value)}
          options={opts(opties.contactmomenten ?? [])}
        />
        {/* Locked server-side for a nazorg-vragenlijst (Jmix parity): doelgroep adoptant, kanaal platform. */}
        <Field label={t("wizards.nazorg.config.doelgroep")} value={def.doelgroep ?? "adoptant"} disabled />
        <Field label={t("wizards.nazorg.config.kanaal")} value={def.kanaal ?? "platform"} disabled />
        <CheckboxField
          label={t("wizards.nazorg.config.autoVersturen")}
          checked={autoVersturen}
          onChange={(e) => setAutoVersturen(e.target.checked)}
        />
        <Field
          label={t("wizards.nazorg.config.herinnerNaDagen")}
          type="number"
          min={1}
          value={herinner}
          onChange={(e) => setHerinner(e.target.value)}
        />
        <Field
          label={t("wizards.nazorg.config.sluitNaDagen")}
          type="number"
          min={1}
          value={sluit}
          onChange={(e) => setSluit(e.target.value)}
        />
      </div>
      <div className="settings-wizard__meta-actions">
        <Button
          onClick={() =>
            onApply({
              actie: "definition.updateNazorg",
              id: def.id,
              contactmoment,
              autoVersturen,
              herinnerNaDagen: dagen(herinner),
              sluitNaDagen: dagen(sluit),
            })
          }
        >
          {t("wizards.nazorg.config.opslaan")}
        </Button>
      </div>
    </>
  );
}

type VeldOptie = { value: string; label: string };

function VlagRegelsEditor({
  def,
  opties,
  veldOpties,
  onApply,
}: {
  def: Definitie;
  opties: Opties;
  veldOpties: VeldOptie[];
  onApply: ApplyFn;
}) {
  const t = useTranslate();
  const regels = def.vlagRegels ?? [];
  const operatoren = opts(opties.vlagOperatoren ?? []);
  const kleuren = opts(opties.vlagKleuren ?? []);
  // New-rule draft; a saved rule row re-renders from the refetched payload.
  const [veld, setVeld] = useState(veldOpties[0]?.value ?? "");
  const [operator, setOperator] = useState("GELIJK_AAN");
  const [waarde, setWaarde] = useState("");
  const [kleur, setKleur] = useState("oranje");
  const gekozenVeld = veldOpties.some((v) => v.value === veld) ? veld : (veldOpties[0]?.value ?? "");

  return (
    <div className="settings-wizard__nazorg-rules">
      {regels.map((r) => (
        <VlagRegelRow
          key={r.id}
          regel={r}
          veldOpties={veldOpties}
          operatoren={operatoren}
          kleuren={kleuren}
          onApply={onApply}
        />
      ))}
      {regels.length === 0 && (
        <p className="fawkes-muted">{t("wizards.nazorg.vlagRegels.leeg")}</p>
      )}
      <div className="settings-wizard__nazorg-add">
        <SelectField
          label={t("wizards.nazorg.col.veld")}
          value={gekozenVeld}
          onChange={(e) => setVeld(e.target.value)}
          options={veldOpties}
        />
        <SelectField
          label={t("wizards.nazorg.col.operator")}
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          options={operatoren}
        />
        <Field
          label={t("wizards.nazorg.col.waarde")}
          value={waarde}
          onChange={(e) => setWaarde(e.target.value)}
        />
        <SelectField
          label={t("wizards.nazorg.col.kleur")}
          value={kleur}
          onChange={(e) => setKleur(e.target.value)}
          options={kleuren}
        />
        <Button
          disabled={!gekozenVeld}
          onClick={() =>
            onApply(
              {
                actie: "nazorgVlag.save",
                definitionId: def.id,
                fieldKey: gekozenVeld,
                operator,
                waarde,
                kleur,
              },
              () => setWaarde(""),
            )
          }
        >
          {t("wizards.nazorg.vlagRegels.toevoegen")}
        </Button>
      </div>
    </div>
  );
}

function VlagRegelRow({
  regel,
  veldOpties,
  operatoren,
  kleuren,
  onApply,
}: {
  regel: VlagRegel;
  veldOpties: VeldOptie[];
  operatoren: VeldOptie[];
  kleuren: VeldOptie[];
  onApply: ApplyFn;
}) {
  const t = useTranslate();
  const [waarde, setWaarde] = useState(regel.waarde ?? "");
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function save(patch: Partial<{ fieldKey: string; operator: string; waarde: string; kleur: string }>) {
    onApply({
      actie: "nazorgVlag.save",
      id: regel.id,
      fieldKey: patch.fieldKey ?? regel.fieldKey,
      operator: patch.operator ?? regel.operator,
      waarde: patch.waarde ?? waarde,
      kleur: patch.kleur ?? regel.kleur,
    });
  }
  function pushWaarde(v: string) {
    setWaarde(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => save({ waarde: v }), 400);
  }

  return (
    <div className="settings-wizard__nazorg-row">
      <SelectField
        label=""
        aria-label={t("wizards.nazorg.col.veld")}
        value={regel.fieldKey}
        onChange={(e) => save({ fieldKey: e.target.value })}
        options={veldOpties}
      />
      <SelectField
        label=""
        aria-label={t("wizards.nazorg.col.operator")}
        value={regel.operator}
        onChange={(e) => save({ operator: e.target.value })}
        options={operatoren}
      />
      <input
        className="fui-field__input"
        aria-label={t("wizards.nazorg.col.waarde")}
        value={waarde}
        onChange={(e) => pushWaarde(e.target.value)}
      />
      <SelectField
        label=""
        aria-label={t("wizards.nazorg.col.kleur")}
        value={regel.kleur}
        onChange={(e) => save({ kleur: e.target.value })}
        options={kleuren}
      />
      <button
        type="button"
        className="settings-wizard__mini settings-wizard__mini--muted"
        title={t("wizards.nazorg.verwijderen")}
        onClick={() => onApply({ actie: "nazorgVlag.delete", id: regel.id })}
      >
        🗑
      </button>
    </div>
  );
}

function ScoreMappingEditor({
  def,
  opties,
  veldOpties,
  onApply,
}: {
  def: Definitie;
  opties: Opties;
  veldOpties: VeldOptie[];
  onApply: ApplyFn;
}) {
  const t = useTranslate();
  const mapping = def.scoreMapping ?? [];
  const dimensies = opts(opties.dimensies ?? []);
  const [veld, setVeld] = useState(veldOpties[0]?.value ?? "");
  const [dimensie, setDimensie] = useState(dimensies[0]?.value ?? "VOEDING");
  const [weging, setWeging] = useState("1");
  const gekozenVeld = veldOpties.some((v) => v.value === veld) ? veld : (veldOpties[0]?.value ?? "");

  return (
    <div className="settings-wizard__nazorg-rules">
      {mapping.map((m) => (
        <ScoreMappingRow
          key={m.id}
          mapping={m}
          veldOpties={veldOpties}
          dimensies={dimensies}
          onApply={onApply}
        />
      ))}
      {mapping.length === 0 && (
        <p className="fawkes-muted">{t("wizards.nazorg.scoreMapping.leeg")}</p>
      )}
      <div className="settings-wizard__nazorg-add">
        <SelectField
          label={t("wizards.nazorg.col.veld")}
          value={gekozenVeld}
          onChange={(e) => setVeld(e.target.value)}
          options={veldOpties}
        />
        <SelectField
          label={t("wizards.nazorg.col.dimensie")}
          value={dimensie}
          onChange={(e) => setDimensie(e.target.value)}
          options={dimensies}
        />
        <Field
          label={t("wizards.nazorg.col.weging")}
          type="number"
          min={1}
          value={weging}
          onChange={(e) => setWeging(e.target.value)}
        />
        <Button
          disabled={!gekozenVeld}
          onClick={() =>
            onApply(
              {
                actie: "nazorgScore.save",
                definitionId: def.id,
                fieldKey: gekozenVeld,
                dimensie,
                weging: Math.max(1, Number.parseInt(weging, 10) || 1),
              },
              () => setWeging("1"),
            )
          }
        >
          {t("wizards.nazorg.scoreMapping.toevoegen")}
        </Button>
      </div>
    </div>
  );
}

function ScoreMappingRow({
  mapping,
  veldOpties,
  dimensies,
  onApply,
}: {
  mapping: ScoreMapping;
  veldOpties: VeldOptie[];
  dimensies: VeldOptie[];
  onApply: ApplyFn;
}) {
  const t = useTranslate();
  const [weging, setWeging] = useState(String(mapping.weging));
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function save(patch: Partial<{ fieldKey: string; dimensie: string; weging: number }>) {
    onApply({
      actie: "nazorgScore.save",
      id: mapping.id,
      fieldKey: patch.fieldKey ?? mapping.fieldKey,
      dimensie: patch.dimensie ?? mapping.dimensie,
      weging: patch.weging ?? (Number.parseInt(weging, 10) || mapping.weging),
    });
  }
  function pushWeging(v: string) {
    setWeging(v);
    if (debounce.current) clearTimeout(debounce.current);
    const n = Number.parseInt(v, 10);
    if (Number.isFinite(n) && n >= 1) {
      debounce.current = setTimeout(() => save({ weging: n }), 400);
    }
  }

  return (
    <div className="settings-wizard__nazorg-row">
      <SelectField
        label=""
        aria-label={t("wizards.nazorg.col.veld")}
        value={mapping.fieldKey}
        onChange={(e) => save({ fieldKey: e.target.value })}
        options={veldOpties}
      />
      <SelectField
        label=""
        aria-label={t("wizards.nazorg.col.dimensie")}
        value={mapping.dimensie}
        onChange={(e) => save({ dimensie: e.target.value })}
        options={dimensies}
      />
      <input
        className="fui-field__input"
        type="number"
        min={1}
        aria-label={t("wizards.nazorg.col.weging")}
        value={weging}
        onChange={(e) => pushWeging(e.target.value)}
      />
      <button
        type="button"
        className="settings-wizard__mini settings-wizard__mini--muted"
        title={t("wizards.nazorg.verwijderen")}
        onClick={() => onApply({ actie: "nazorgScore.delete", id: mapping.id })}
      >
        🗑
      </button>
    </div>
  );
}

// ── Tab: Formulier bouwen (palette + canvas, native HTML5 drag-and-drop) ──────────
function FormulierTab({ def, onApply }: { def: Definitie; onApply: ApplyFn }) {
  const stappen = def.stappen;
  const [activeStepId, setActiveStepId] = useState<string | null>(stappen[0]?.id ?? null);
  const activeExists = stappen.some((s) => s.id === activeStepId);
  const huidigeStap = activeExists ? activeStepId : (stappen[stappen.length - 1]?.id ?? null);
  const activeTitle = stappen.find((s) => s.id === huidigeStap)?.title;

  function voegVeldToe(stepId: string | null, fieldType: string) {
    if (!stepId) return;
    onApply({ actie: "field.add", stepId, label: veldtype(fieldType).naam, fieldType });
  }
  function voegStapToe() {
    onApply({ actie: "step.add", definitionId: def.id, title: "Nieuwe stap" }, (id) => {
      if (id) setActiveStepId(id);
    });
  }

  return (
    <div className="settings-wizard__builder">
      <aside className="settings-wizard__palette">
        <div className="settings-wizard__palette-title">Bouwstenen</div>
        <p className="settings-wizard__palette-help">
          Sleep een blok naar een onderdeel — of klik om het toe te voegen.
        </p>
        {huidigeStap && (
          <div className="settings-wizard__palette-target">
            Klik voegt toe aan: <strong>{activeTitle || "dit onderdeel"}</strong>
          </div>
        )}
        {VELDTYPES.map((vt) => (
          <button
            key={vt.id}
            type="button"
            draggable
            className={`settings-wizard__palette-block ${huidigeStap ? "" : "settings-wizard__palette-block--off"}`}
            onDragStart={(e) => {
              e.dataTransfer.setData(DT_NEW, vt.id);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => voegVeldToe(huidigeStap, vt.id)}
            title={
              huidigeStap
                ? "Klik om toe te voegen aan het gekozen onderdeel"
                : "Maak eerst een onderdeel"
            }
          >
            <span className="settings-wizard__palette-icon" aria-hidden="true">
              {vt.icon}
            </span>
            <span className="settings-wizard__palette-text">
              <strong>{vt.naam}</strong>
              <span className="settings-wizard__palette-hint">{vt.hint}</span>
            </span>
          </button>
        ))}
        <Button variant="secondary" onClick={voegStapToe}>
          + Nieuw onderdeel
        </Button>
      </aside>

      <div className="settings-wizard__canvas">
        {stappen.length === 0 ? (
          <div className="settings-wizard__empty-canvas">
            <div className="settings-wizard__empty-icon" aria-hidden="true">
              🧩
            </div>
            <p>
              Begin met een <strong>onderdeel</strong> (bijv. &quot;Algemeen&quot;) en sleep er
              bouwstenen in.
            </p>
            <Button onClick={voegStapToe}>+ Eerste onderdeel</Button>
          </div>
        ) : (
          stappen.map((s, i) => (
            <Onderdeel
              key={s.id}
              stap={s}
              nr={i + 1}
              isFirst={i === 0}
              isLast={i === stappen.length - 1}
              actief={huidigeStap === s.id}
              onActiveer={() => setActiveStepId(s.id)}
              onVeldToe={voegVeldToe}
              onApply={onApply}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Onderdeel({
  stap,
  nr,
  isFirst,
  isLast,
  actief,
  onActiveer,
  onVeldToe,
  onApply,
}: {
  stap: Stap;
  nr: number;
  isFirst: boolean;
  isLast: boolean;
  actief: boolean;
  onActiveer: () => void;
  onVeldToe: (stepId: string, fieldType: string) => void;
  onApply: ApplyFn;
}) {
  const [title, setTitle] = useState(stap.title);
  const [sleepOver, setSleepOver] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const velden = stap.velden;

  function pushTitle(v: string) {
    setTitle(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => onApply({ actie: "step.update", id: stap.id, title: v }), 400);
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setSleepOver(false);
    const nieuw = e.dataTransfer.getData(DT_NEW);
    if (nieuw) {
      onVeldToe(stap.id, nieuw);
      return;
    }
    const moveId = e.dataTransfer.getData(DT_MOVE);
    if (moveId && !velden.some((v) => v.id === moveId)) {
      onApply({ actie: "field.moveToStep", id: moveId, stepId: stap.id });
    }
  }
  function herorden(moveId: string, voorId: string | null) {
    const ids = velden.map((v) => v.id).filter((x) => x !== moveId);
    const pos = voorId ? ids.indexOf(voorId) : ids.length;
    ids.splice(pos < 0 ? ids.length : pos, 0, moveId);
    onApply({ actie: "field.reorder", stepId: stap.id, orderedIds: ids });
  }

  return (
    <div
      className={`settings-wizard__onderdeel ${actief ? "settings-wizard__onderdeel--actief" : ""} ${sleepOver ? "settings-wizard__onderdeel--over" : ""}`}
      onClick={onActiveer}
      onDragOver={(e) => {
        e.preventDefault();
        setSleepOver(true);
      }}
      onDragLeave={() => setSleepOver(false)}
      onDrop={onDrop}
    >
      <div className="settings-wizard__onderdeel-head">
        <span className="settings-wizard__step-nr" aria-hidden="true">
          {nr}
        </span>
        <input
          className="fui-field__input settings-wizard__onderdeel-title"
          value={title}
          onChange={(e) => pushTitle(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="Naam van dit onderdeel"
          aria-label="Naam van dit onderdeel"
        />
        <button
          type="button"
          className="settings-wizard__mini"
          disabled={isFirst}
          title="Onderdeel omhoog"
          onClick={(e) => {
            e.stopPropagation();
            onApply({ actie: "step.move", id: stap.id, richting: "up" });
          }}
        >
          ↑
        </button>
        <button
          type="button"
          className="settings-wizard__mini"
          disabled={isLast}
          title="Onderdeel omlaag"
          onClick={(e) => {
            e.stopPropagation();
            onApply({ actie: "step.move", id: stap.id, richting: "down" });
          }}
        >
          ↓
        </button>
        <button
          type="button"
          className="settings-wizard__mini settings-wizard__mini--muted"
          title="Onderdeel verwijderen"
          onClick={(e) => {
            e.stopPropagation();
            onApply({ actie: "step.delete", id: stap.id });
          }}
        >
          🗑
        </button>
      </div>

      <div className="settings-wizard__vragen">
        {velden.length === 0 && (
          <div className="settings-wizard__vragen-leeg">
            Sleep hier bouwstenen naartoe, of klik links op een blok.
          </div>
        )}
        {velden.map((f, i) => (
          <Vraag
            key={f.id}
            f={f}
            isFirst={i === 0}
            isLast={i === velden.length - 1}
            onHerorden={herorden}
            onApply={onApply}
          />
        ))}
      </div>
    </div>
  );
}

function Vraag({
  f,
  isFirst,
  isLast,
  onHerorden,
  onApply,
}: {
  f: Veld;
  isFirst: boolean;
  isLast: boolean;
  onHerorden: (moveId: string, voorId: string | null) => void;
  onApply: ApplyFn;
}) {
  const [label, setLabel] = useState(f.label);
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vt = veldtype(f.fieldType);

  function pushLabel(v: string) {
    setLabel(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => onApply({ actie: "field.update", id: f.id, label: v }), 400);
  }

  return (
    <div
      className={`settings-wizard__vraag ${over ? "settings-wizard__vraag--over" : ""}`}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData(DT_MOVE, f.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DT_MOVE)) {
          e.preventDefault();
          e.stopPropagation();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        const moveId = e.dataTransfer.getData(DT_MOVE);
        if (moveId) {
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
          if (moveId !== f.id) onHerorden(moveId, f.id);
        }
      }}
    >
      <div className="settings-wizard__vraag-row">
        <span className="settings-wizard__grip" title="Sleep om te verplaatsen" aria-hidden="true">
          ⠿
        </span>
        <span className="settings-wizard__field-type" title={vt.naam} aria-hidden="true">
          {FIELD_TYPE_ICON[f.fieldType] ?? "📝"}
        </span>
        <input
          className="fui-field__input settings-wizard__vlabel"
          value={label}
          onChange={(e) => pushLabel(e.target.value)}
          placeholder="Typ hier de vraag…"
          aria-label="Vraag / label"
        />
        {f.required && <StatusBadge tone="success">verplicht</StatusBadge>}
        <button
          type="button"
          className="settings-wizard__mini"
          disabled={isFirst}
          title="Omhoog"
          onClick={() => onApply({ actie: "field.move", id: f.id, richting: "up" })}
        >
          ↑
        </button>
        <button
          type="button"
          className="settings-wizard__mini"
          disabled={isLast}
          title="Omlaag"
          onClick={() => onApply({ actie: "field.move", id: f.id, richting: "down" })}
        >
          ↓
        </button>
        <button
          type="button"
          className={`settings-wizard__mini ${open ? "settings-wizard__mini--on" : ""}`}
          title="Instellingen"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          ⚙
        </button>
        <button
          type="button"
          className="settings-wizard__mini settings-wizard__mini--muted"
          title="Verwijderen"
          onClick={() => onApply({ actie: "field.delete", id: f.id })}
        >
          🗑
        </button>
      </div>
      {open && <VraagInstellingen f={f} onApply={onApply} />}
    </div>
  );
}

/**
 * De keuzes van een vraag als tekst, één per regel.
 *
 * <p><b>Twee vormen, allebei in gebruik.</b> Dit scherm schrijft paren weg (`{label, value}`), maar
 * in de database staan ook lijsten van kale teksten — uit seeds en uit eerdere versies. Alleen de
 * paarvorm lezen betekende dat zo'n vraag hier met een leeg keuzeveld opende terwijl de keuzes er
 * wel degelijk waren (Maarten, 26-10-2026), en wie dan opsloeg gooide ze weg.
 */
function keuzeLabel(optie: Veld["options"][number]): string {
  return typeof optie === "string" ? optie : (optie?.label ?? optie?.value ?? "");
}

function keuzeWaarde(optie: Veld["options"][number]): string {
  return typeof optie === "string" ? optie : (optie?.value ?? optie?.label ?? "");
}

function keuzeTekst(opties: Veld["options"] | undefined): string {
  return (opties ?? [])
    .map(keuzeLabel)
    .filter((o) => o.trim() !== "")
    .join("\n");
}

function VraagInstellingen({ f, onApply }: { f: Veld; onApply: ApplyFn }) {
  const heeftOpties = f.fieldType === "SELECT" || f.fieldType === "MULTISELECT";
  const [helpText, setHelpText] = useState(f.helpText ?? "");
  const [optionsText, setOptionsText] = useState(keuzeTekst(f.options));
  const debHelp = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debOpt = useRef<ReturnType<typeof setTimeout> | null>(null);

  function pushHelp(v: string) {
    setHelpText(v);
    if (debHelp.current) clearTimeout(debHelp.current);
    debHelp.current = setTimeout(() => onApply({ actie: "field.update", id: f.id, helpText: v }), 400);
  }
  function pushOpts(v: string) {
    setOptionsText(v);
    if (debOpt.current) clearTimeout(debOpt.current);
    const options = v
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((l) => ({ label: l, value: l }));
    debOpt.current = setTimeout(() => onApply({ actie: "field.update", id: f.id, options }), 500);
  }

  return (
    <div className="settings-wizard__vraag-inst">
      <CheckboxField
        label="Verplicht invullen"
        checked={Boolean(f.required)}
        onChange={(e) => onApply({ actie: "field.update", id: f.id, required: e.target.checked })}
      />
      <SelectField
        label="Koppel aan dier-gegeven (optioneel)"
        value={f.target ?? ""}
        onChange={(e) => onApply({ actie: "field.update", id: f.id, target: e.target.value })}
        options={DOELVELDEN}
      />
      {heeftOpties && (
        <TextareaField
          label="Keuzes (één per regel)"
          value={optionsText}
          onChange={(e) => pushOpts(e.target.value)}
          placeholder={"Laag\nGemiddeld\nHoog"}
        />
      )}
      {heeftOpties && (
        <CheckboxField
          label="Toon als beoordelingsschaal (smileys + kleur 😄→😠)"
          checked={f.fieldType === "SELECT" ? f.scale !== false : f.scale === true}
          onChange={(e) => onApply({ actie: "field.update", id: f.id, scale: e.target.checked })}
        />
      )}
      <Field
        label="Hulptekst onder de vraag (optioneel)"
        value={helpText}
        onChange={(e) => pushHelp(e.target.value)}
        placeholder="korte toelichting voor de invuller"
      />
    </div>
  );
}

// ── Tab: Voorbeeld (read-only render of the form the medewerker will fill in) ─────
function PreviewTab({ def }: { def: Definitie }) {
  const stappen = def.stappen;
  if (stappen.length === 0) {
    return <p className="fawkes-muted">Voeg stappen en velden toe om een voorbeeld te zien.</p>;
  }
  return (
    <div className="settings-wizard__preview">
      {stappen.map((s, i) => (
        <div key={s.id} className="settings-wizard__preview-step">
          <div className="settings-wizard__preview-step-head">
            Stap {i + 1}: {s.title}
          </div>
          {s.description && <p className="fawkes-muted">{s.description}</p>}
          {s.velden.map((f) => (
            <label key={f.id} className="settings-wizard__preview-field">
              <span>
                {f.label}
                {f.required && <span className="settings-wizard__preview-star">*</span>}
              </span>
              <VeldPreview f={f} />
              {f.helpText && <span className="fawkes-muted">{f.helpText}</span>}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function VeldPreview({ f }: { f: Veld }) {
  switch (f.fieldType) {
    case "TEXTAREA":
      return <textarea className="fui-field__input" rows={2} disabled />;
    case "NUMBER":
      return <input className="fui-field__input" type="number" disabled />;
    case "BOOLEAN":
      return (
        <span className="settings-wizard__preview-check">
          <input type="checkbox" disabled /> Ja
        </span>
      );
    case "DATE":
      return <DateField className="fui-field__input" disabled />;
    case "SELECT":
      return (
        <select className="fui-field__input" disabled>
          <option>Kies…</option>
          {(f.options ?? []).map((o, i) => (
            <option key={i}>{keuzeLabel(o)}</option>
          ))}
        </select>
      );
    case "MULTISELECT":
      return (
        <span className="settings-wizard__preview-checks">
          {(f.options ?? []).map((o, i) => (
            <span key={i} className="settings-wizard__preview-check">
              <input type="checkbox" disabled /> {keuzeLabel(o)}
            </span>
          ))}
        </span>
      );
    default:
      return <input className="fui-field__input" disabled />;
  }
}

function TileEditor({ def, onApply }: { def: Definitie; onApply: ApplyFn }) {
  const [title, setTitle] = useState(def.tegel?.title ?? "");
  const [icon, setIcon] = useState(def.tegel?.icon ?? "");
  const [visible, setVisible] = useState<string[]>(def.tegel?.visibleFieldKeys ?? []);
  const [statusVeld, setStatusVeld] = useState(def.tegel?.statusRule?.fieldKey ?? "");
  const [statusMap, setStatusMap] = useState<Record<string, StatusRuleEntry>>(
    def.tegel?.statusRule?.map ?? {},
  );
  const allFields = def.stappen.flatMap((s) => s.velden);
  // A status indicator can be driven by a ja/nee (BOOLEAN) or keuzelijst (SELECT) field.
  const statusKandidaten = allFields.filter(
    (f) => f.fieldType === "BOOLEAN" || f.fieldType === "SELECT",
  );

  function toggle(key: string, on: boolean) {
    setVisible((prev) => (on ? [...prev, key] : prev.filter((k) => k !== key)));
  }
  function waardesVan(fieldKey: string): Array<{ value: string; label: string }> {
    const f = allFields.find((x) => x.fieldKey === fieldKey);
    if (!f) return [];
    if (f.fieldType === "BOOLEAN") {
      return [
        { value: "true", label: "Ja" },
        { value: "false", label: "Nee" },
      ];
    }
    return (f.options ?? []).map((o) => ({ value: keuzeWaarde(o), label: keuzeLabel(o) }));
  }
  function kiesStatusVeld(fieldKey: string) {
    setStatusVeld(fieldKey);
    if (fieldKey) {
      const map: Record<string, StatusRuleEntry> = {};
      waardesVan(fieldKey).forEach((w) => {
        map[w.value] = statusMap[w.value] ?? { kleur: "grijs", label: w.label };
      });
      setStatusMap(map);
    }
  }
  function setKleur(value: string, kleur: string) {
    setStatusMap((cur) => ({ ...cur, [value]: { label: cur[value]?.label ?? "", kleur } }));
  }
  function setLabel(value: string, label: string) {
    setStatusMap((cur) => ({ ...cur, [value]: { kleur: cur[value]?.kleur ?? "grijs", label } }));
  }
  function opslaan() {
    const statusRule = statusVeld
      ? { fieldKey: statusVeld, map: statusMap, default: { kleur: "grijs", label: "—" } }
      : {};
    onApply({
      actie: "tile.save",
      definitionId: def.id,
      title,
      icon,
      visibleFieldKeys: visible,
      statusRule,
    });
  }

  return (
    <>
      <SectionHeader>Tegel op het dier</SectionHeader>
      <p className="fawkes-muted">
        Kies wat er op de tegel van het dier/verblijf verschijnt na een registratie.
      </p>
      <div className="fawkes-form-grid">
        <Field label="Tegeltitel" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Field label="Icoon (emoji)" value={icon} onChange={(e) => setIcon(e.target.value)} />
      </div>

      <div className="settings-wizard__status-config">
        <span className="settings-wizard__status-config-label">
          Statusindicator (optioneel) — een gekleurde score die in één oogopslag iets zegt
        </span>
        {statusKandidaten.length === 0 ? (
          <p className="fawkes-muted">
            Voeg eerst een ja/nee- of keuzelijst-veld toe om een status te kunnen tonen.
          </p>
        ) : (
          <SelectField
            label="Stuur de status op basis van"
            value={statusVeld}
            onChange={(e) => kiesStatusVeld(e.target.value)}
            options={[
              { value: "", label: "Geen statusindicator" },
              ...statusKandidaten.map((f) => ({ value: f.fieldKey, label: f.label })),
            ]}
          />
        )}
        {statusVeld && (
          <div className="settings-wizard__status-rules">
            {waardesVan(statusVeld).map((w) => (
              <div key={w.value} className="settings-wizard__status-row">
                <span
                  className={`settings-wizard__status-dot settings-wizard__status-dot--${statusMap[w.value]?.kleur ?? "grijs"}`}
                  aria-hidden="true"
                />
                <span className="settings-wizard__status-value">{w.label}</span>
                <input
                  className="fui-field__input settings-wizard__status-label"
                  value={statusMap[w.value]?.label ?? w.label}
                  onChange={(e) => setLabel(w.value, e.target.value)}
                  placeholder="Tekst op de tegel"
                  aria-label={`Tekst op de tegel voor ${w.label}`}
                />
                <select
                  className="fui-field__input settings-wizard__status-kleur"
                  value={statusMap[w.value]?.kleur ?? "grijs"}
                  onChange={(e) => setKleur(w.value, e.target.value)}
                  aria-label={`Kleur voor ${w.label}`}
                >
                  {STATUS_KLEUREN.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="fawkes-muted">Zichtbare velden op de tegel:</p>
      <div className="settings-wizard__tile-fields">
        {allFields.map((f) => (
          <CheckboxField
            key={f.id}
            label={f.label}
            checked={visible.includes(f.fieldKey)}
            onChange={(e) => toggle(f.fieldKey, e.target.checked)}
          />
        ))}
        {allFields.length === 0 && <span className="fawkes-muted">Voeg eerst velden toe.</span>}
      </div>
      <ActionBar>
        <Button onClick={opslaan}>Tegel opslaan</Button>
      </ActionBar>
    </>
  );
}
